<template>
  <main class="voting-page voting-dashboard">
    <header class="voting-page__header">
      <div>
        <p class="voting-page__eyebrow">Participación de miembro</p>
        <h1>Mis votaciones</h1>
        <p class="voting-page__intro">
          Consulta los procesos disponibles para tu SOFOM, quién está autorizado y el estado de su participación.
        </p>
      </div>
      <button type="button" class="voting-refresh" :disabled="loading" @click="loadProcesses">
        <span :class="{ 'voting-refresh__spin': loading }" aria-hidden="true">&#8635;</span>
        Actualizar
      </button>
    </header>

    <div v-if="error" class="voting-alert voting-alert--error" role="alert">
      <strong>No se pudo cargar el dashboard</strong>
      <span>{{ error }}</span>
      <button type="button" @click="loadProcesses">Reintentar</button>
    </div>

    <div v-if="loading" class="voting-loading" aria-live="polite">
      <span class="voting-loading__spinner" aria-hidden="true"></span>
      <span>Cargando tus procesos de votación...</span>
    </div>

    <section v-else-if="!error && !processes.length" class="voting-empty">
      <div class="voting-empty__icon" aria-hidden="true">&#10003;</div>
      <h2>No tienes votaciones disponibles</h2>
      <p>Cuando ASOFOM habilite un proceso para tu SOFOM, aparecerá aquí.</p>
    </section>

    <section v-else class="voting-process-list" aria-label="Procesos de votación">
      <article v-for="process in processes" :key="process.id" class="voting-process-card">
        <div class="voting-process-card__content">
          <div class="voting-process-card__meta">
            <span>{{ getVotingTypeLabel(process.typeKey) }}</span>
            <span class="voting-status" :class="statusClass(process)">
              {{ statusLabel(process.status, hasParticipated(process), process.isAuthorized) }}
            </span>
          </div>
          <h2>{{ process.title }}</h2>
          <p v-if="process.description" class="voting-process-card__description">
            {{ process.description }}
          </p>
          <p v-if="process.closesAt" class="voting-process-card__date">
            <strong>Cierre:</strong> {{ formatDateTime(process.closesAt) }}
          </p>

          <aside
            v-if="!process.isAuthorized || hasParticipated(process)"
            class="voting-authorized-voter"
            :aria-label="hasParticipated(process) ? 'Identidad asociada al voto' : 'Persona autorizada para votar'"
          >
            <div>
              <span class="voting-authorized-voter__label">
                {{ hasParticipated(process) ? "Voto registrado a nombre de" : "Persona autorizada para votar" }}
              </span>
              <strong>{{ voterIdentity(process).name || "No hay una persona autorizada registrada" }}</strong>
              <a
                v-if="voterIdentity(process).email"
                :href="`mailto:${voterIdentity(process).email}`"
              >
                {{ voterIdentity(process).email }}
              </a>
            </div>
            <span class="voting-authorized-voter__status">
              {{ hasParticipated(process) ? "La participación de tu empresa ya fue registrada." : "Tu empresa aún no registra su voto." }}
            </span>
          </aside>

          <div
            v-if="hasParticipated(process) || isResultsPublished(process)"
            class="voting-participated"
          >
            <div v-if="hasParticipated(process)">
              <strong>{{ process.isAuthorized ? "Tu voto fue registrado" : "El voto de tu empresa fue registrado" }}</strong>
              <span v-if="process.ballotSubmittedAt">
                Registrado el {{ formatDateTime(process.ballotSubmittedAt) }}.
              </span>
              <span
                v-if="
                  process.status !== 'PUBLISHED' &&
                  !process.resultsPublished &&
                  process.resultsPublishAt
                "
              >
                Resultados disponibles el {{ formatDateTime(process.resultsPublishAt) }}.
              </span>
            </div>
            <a
              v-if="process.shareUrl"
              class="voting-result-link"
              :href="getLocalVotingResultsUrl(process.shareUrl)"
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver resultados públicos <span aria-hidden="true">&#8594;</span>
            </a>
            <span v-else class="voting-process-card__muted">
              {{ isResultsPublished(process) ? "El enlace público no está disponible." : "El enlace público estará disponible cuando se prepare el resultado." }}
            </span>
          </div>
        </div>

        <button
          v-if="!hasParticipated(process) && process.isAuthorized && process.canVote"
          type="button"
          class="voting-button voting-button--primary voting-process-card__action"
          :disabled="process.status !== 'OPEN' || openingId === process.id"
          @click="openProcess(process)"
        >
          <span v-if="openingId === process.id" class="voting-button__spinner" aria-hidden="true"></span>
          {{ process.status === 'OPEN' ? 'Abrir votación' : statusLabel(process.status, false) }}
        </button>
      </article>
    </section>
  </main>
</template>

<script>
import { getVotingErrorMessage, votingService } from "@/api/voting";
import {
  formatVotingDate,
  getLocalVotingResultsUrl,
  getVotingTypeLabel,
} from "@/share/voting";

export default {
  name: "MemberVotingDashboard",
  data() {
    return {
      processes: [],
      loading: true,
      error: "",
      openingId: null,
    };
  },
  computed: {
    ballotRouteName() {
      return this.$route.path.indexOf("/member-v2/") === 0
        ? "Member V2 Voting Ballot"
        : "Member Voting Ballot";
    },
  },
  created() {
    this.loadProcesses();
  },
  methods: {
    getVotingTypeLabel,
    formatDateTime: formatVotingDate,
    getLocalVotingResultsUrl,
    async loadProcesses() {
      this.loading = true;
      this.error = "";
      try {
        const response = await votingService.listProcesses();
        this.processes = response.items || [];
      } catch (error) {
        this.error = getVotingErrorMessage(
          error,
          "No se pudieron cargar tus procesos de votación."
        );
      } finally {
        this.loading = false;
      }
    },
    hasParticipated(process) {
      return process.hasVoted || process.ballotStatus === "SUBMITTED";
    },
    isResultsPublished(process) {
      return Boolean(process.resultsPublished || process.status === "PUBLISHED");
    },
    voterIdentity(process) {
      if (this.hasParticipated(process) && process.votedBy) {
        return process.votedBy;
      }
      return process.authorizedVoter || {};
    },
    statusLabel(status, participated, isAuthorized) {
      if (participated) {
        const participant = isAuthorized ? "Participaste" : "Tu empresa participó";
        return status === "PUBLISHED"
          ? `${participant} · Resultados publicados`
          : participant;
      }
      return (
        {
          OPEN: "Votación abierta",
          SCHEDULED: "Próximamente",
          CLOSED: "Votación cerrada",
          PUBLISHED: "Resultados publicados",
        }[status] || "Votación"
      );
    },
    statusClass(process) {
      if (this.hasParticipated(process)) return "voting-status--complete";
      return `voting-status--${String(process.status || "").toLowerCase()}`;
    },
    async openProcess(process) {
      if (process.status !== "OPEN" || this.openingId) return;
      this.openingId = process.id;
      this.error = "";
      try {
        await votingService.openProcess(process.id);
        await this.$router.push({
          name: this.ballotRouteName,
          params: { electionId: process.id },
        });
      } catch (error) {
        this.error = getVotingErrorMessage(
          error,
          "No se pudo abrir la boleta de este proceso."
        );
      } finally {
        this.openingId = null;
      }
    },
  },
};
</script>

<style scoped>
.voting-page {
  min-height: calc(100vh - 56px);
  box-sizing: border-box;
  max-width: 980px;
  margin: 0 auto;
  padding: 32px 28px 64px;
  color: #1b273e;
}

.voting-page__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 30px;
}

.voting-page__eyebrow {
  margin: 0;
  color: #a66f00;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.voting-page h1 {
  margin: 8px 0 0;
  color: #1b273e;
  font-size: clamp(28px, 4vw, 42px);
  line-height: 1.12;
}

.voting-page__intro {
  max-width: 680px;
  margin: 12px 0 0;
  color: #687386;
  font-size: 15px;
  line-height: 1.65;
}

.voting-refresh {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  border: 1px solid #cbd3df;
  border-radius: 999px;
  padding: 9px 15px;
  background: #ffffff;
  color: #1b273e;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.voting-refresh:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.voting-refresh__spin {
  display: inline-block;
  animation: voting-spin 700ms linear infinite;
}

.voting-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 22px;
  border-radius: 14px;
  padding: 14px 16px;
  font-size: 14px;
}

.voting-alert--error {
  border: 1px solid #f3c3c0;
  background: #fff2f1;
  color: #8d211b;
}

.voting-alert span {
  flex: 1;
}

.voting-alert button {
  border: 0;
  background: transparent;
  color: inherit;
  font-weight: 800;
  text-decoration: underline;
  cursor: pointer;
}

.voting-loading,
.voting-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  border: 1px dashed #cbd3df;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.7);
  color: #687386;
  text-align: center;
}

.voting-loading {
  flex-direction: row;
  gap: 12px;
  min-height: 180px;
  border-style: solid;
}

.voting-loading__spinner,
.voting-button__spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(27, 39, 62, 0.25);
  border-top-color: #1b273e;
  border-radius: 50%;
  animation: voting-spin 700ms linear infinite;
}

.voting-empty__icon {
  display: flex;
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eef3fb;
  color: #1b273e;
  font-size: 24px;
  font-weight: 900;
}

.voting-empty h2 {
  margin: 18px 0 0;
  font-size: 21px;
}

.voting-empty p {
  margin: 8px 0 0;
  font-size: 14px;
}

.voting-process-list {
  display: grid;
  gap: 16px;
}

.voting-process-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  border: 1px solid #dfe4eb;
  border-radius: 20px;
  padding: 22px;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(27, 39, 62, 0.06);
}

.voting-process-card__content {
  min-width: 0;
  flex: 1;
}

.voting-process-card__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #a66f00;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.voting-process-card h2 {
  margin: 8px 0 0;
  color: #1b273e;
  font-size: 21px;
  line-height: 1.3;
}

.voting-process-card__description,
.voting-process-card__date {
  margin: 8px 0 0;
  color: #687386;
  font-size: 14px;
  line-height: 1.55;
}

.voting-process-card__date {
  margin-top: 14px;
  font-size: 13px;
}

.voting-status {
  border-radius: 999px;
  padding: 5px 9px;
  letter-spacing: 0;
  text-transform: none;
}

.voting-status--open {
  background: #e7f6ea;
  color: #237b37;
}

.voting-status--scheduled {
  background: #eef3fb;
  color: #31527d;
}

.voting-status--closed,
.voting-status--published {
  background: #f1f4f8;
  color: #687386;
}

.voting-status--complete {
  background: #fff3d6;
  color: #8b6000;
}

.voting-authorized-voter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 20px;
  margin-top: 16px;
  border: 1px solid #d7dfeb;
  border-radius: 12px;
  padding: 12px 14px;
  background: #f7f9fc;
  color: #1b273e;
  font-size: 13px;
}

.voting-authorized-voter__label,
.voting-authorized-voter strong,
.voting-authorized-voter a {
  display: block;
}

.voting-authorized-voter__label {
  margin-bottom: 4px;
  color: #687386;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.voting-authorized-voter a {
  margin-top: 3px;
  color: #31527d;
  overflow-wrap: anywhere;
  text-decoration: underline;
}

.voting-authorized-voter__status {
  color: #687386;
}

.voting-participated {
  display: grid;
  gap: 8px;
  margin-top: 16px;
  border: 1px solid #d8ecd9;
  border-radius: 12px;
  padding: 12px 14px;
  background: #f5fbf5;
  color: #237b37;
  font-size: 13px;
}

.voting-participated strong,
.voting-participated span {
  display: block;
}

.voting-participated span {
  margin-top: 4px;
  color: #687386;
}

.voting-result-link {
  justify-self: start;
  margin-top: 3px;
  color: #1b273e;
  font-weight: 800;
  text-decoration: none;
}

.voting-result-link:hover {
  text-decoration: underline;
}

.voting-process-card__muted {
  color: #687386 !important;
  font-size: 12px;
}

.voting-button {
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  border-radius: 999px;
  padding: 11px 18px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
}

.voting-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.voting-button--primary {
  background: #ffb500;
  color: #1b273e;
}

.voting-process-card__action {
  flex: 0 0 auto;
}

@keyframes voting-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 700px) {
  .voting-page {
    padding: 24px 16px 48px;
  }

  .voting-page__header,
  .voting-process-card {
    align-items: stretch;
    flex-direction: column;
  }

  .voting-refresh,
  .voting-process-card__action {
    align-self: flex-start;
  }

  .voting-alert {
    align-items: flex-start;
    flex-wrap: wrap;
  }
}
</style>
